<template>
  <v-container>
    <PageHeader subtitle="Manage media types across the system." />
    <Subheader v-model="search" title="Media Types" search="media types">
      <v-btn color="success" @click="showAddType = true">
        <v-icon class="mr-1">add</v-icon>
        {{ $t('Button.Add_a_new_Media_Type') }}
      </v-btn>
    </Subheader>
    <v-card>
      <v-card-text>
        <v-data-table :headers="headers" :items="MediaTypeList" :pagination.sync="pagination" :search="search">
          <template slot="items" slot-scope="props">
            <td>
              <v-icon class="mr-1">{{ props.item.IconPath }}</v-icon>
              {{ props.item.Name }}
            </td>
            <td>{{ props.item.IconPath }}</td>
            <td>{{ props.item.TenantName }}</td>
            <td class="text-xs-right">
              <v-btn small depressed @click="clickEditType(props.item)">{{ $t('Button.Manage_Type') }}</v-btn>
              <v-btn small depressed color="error" @click="clickRemove(props.item)">
                <v-icon>delete_forever</v-icon>
                {{ $t('Button.Remove') }}
              </v-btn>
            </td>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="showAddType" persistent max-width="800px">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ $t('AdministrationMediaTypeManage.addTitleMsg') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click.native="showAddType = false" dark>
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-form v-model="valid" ref="form" lazy-validation class="fullScreenWidth">
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12 pa-3>
                <v-text-field label="Media Type Name" v-model="newMediaType.name" :rules="nameRules"></v-text-field>
                <v-select label="Icon Path" v-model="newMediaType.IconPath" :items="iconList">
                  <template slot="selection" slot-scope="data">
                    <v-avatar>
                      <v-icon>{{data.item}}</v-icon>
                    </v-avatar>
                    {{ data.item }}
                  </template>
                  <template slot="item" slot-scope="data">
                    <v-list-tile-avatar>
                      <v-icon>{{data.item}}</v-icon>
                    </v-list-tile-avatar>
                    <v-list-tile-content>{{data.item}}</v-list-tile-content>
                  </template>
                </v-select>
                <v-combobox label="Tenant" v-model="newMediaType.TenantId" :items="allRoleList" item-text="RoleName" item-value="Id" :rules="tenantRules"></v-combobox>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="success" @click="addNewType()">
              <v-icon class="mr-1">done</v-icon>
              {{ $t('Button.Save') }}
            </v-btn>
            <v-btn color="secondary" outline @click="clearForm()">{{ $t('Button.Close') }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showUpdateType" persistent max-width="800px">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{ $t('AdministrationMediaTypeManage.editTitleMsg') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click.native="showUpdateType = false" dark>
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-form v-model="valid2" ref="form2" lazy-validation class="fullScreenWidth">
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12 pa-3>
                <v-text-field label="Media Type Name" v-model="editMediaType.Name" :rules="editNameRules"></v-text-field>
                <v-select label="Icon Path" v-model="editMediaType.IconPath" :items="iconList">
                  <template slot="selection" slot-scope="data">
                    <v-avatar>
                      <v-icon>{{data.item}}</v-icon>
                    </v-avatar>
                    {{ data.item }}
                  </template>
                  <template slot="item" slot-scope="data">
                    <v-list-tile-avatar>
                      <v-icon>{{data.item}}</v-icon>
                    </v-list-tile-avatar>
                    <v-list-tile-content>{{data.item}}</v-list-tile-content>
                  </template>
                </v-select>
                <v-combobox label="Tenant" v-model="editMediaType.Tenant" :items="allRoleList" item-text="RoleName" item-value="Id" :rules="tenantRules" return-object></v-combobox>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="success" @click="updateMediaType()">
              <v-icon class="mr-1">done</v-icon>
              {{ $t('Button.Save') }}
            </v-btn>
            <v-btn color="secondary" outline @click="clearForm()">{{ $t('Button.Close') }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <AppModal v-model="dialog" max-width="800">
      <template v-slot:title>{{ deleteDialogTitle }}</template>
      <template v-slot:text>{{ deleteDialogText }}</template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="delete" dark @click="agreeDelete()" :disabled="!isDeleteable">
          <v-icon>delete_forever</v-icon>{{ $t('Button.Remove') }}
        </v-btn>
        <v-btn color="secondary" @click="dialog = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </AppModal>

    <AppConfirmationModal v-model="cannotRemove" max-width="500">
      <template slot="title">
        {{ cannotDeleteDialogTitle }}
      </template>
      <template slot="text">
        {{ cannotDeleteDialogText }}
      </template>
      <template slot="actions">
        <v-spacer></v-spacer>
        <v-btn color="secondary" @click="cannotRemove = false" outline>{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </AppConfirmationModal>

    <v-card>
      <v-snackbar class="snackbarAdjust" :color="snackColor" :timeout="null" top v-model="snackbar">
        {{ snackMessage }}
        <v-btn dark flat @click.native="snackbar = false">{{ $t('Button.Close') }}</v-btn>
      </v-snackbar>
    </v-card>
  </v-container>
</template>

<script>
  import axios from 'axios'
  import { createNamespacedHelpers } from 'vuex'
  const stations = createNamespacedHelpers('stations')
  const admin = createNamespacedHelpers('admin')
  export default {
    data() {
      return {
        search: '',
        pagination: {
          rowsPerPage: 10,
          sortBy: 'TenantName',
        },
        headers: [{
          align: 'left',
          text: 'Media Type Name',
          value: 'Name',
        },
        {
          align: 'left',
          text: 'Icon Path',
          value: 'IconPath',
        },
        {
          align: 'left',
          text: 'Tenant',
          value: 'TenantName',
        },
        {
          sortable: false,
          text: '',
          value: '',
        },
        ],
        dialog: false,
        showAddType: false,
        showUpdateType: false,
        snackbar: false,
        snackColor: '',
        snackMessage: '',
        valid: true,
        valid2: true,
        cannotRemove: false,
        newMediaType: {},
        editMediaType: {},
        typeListWithoutEdit: [],
        deleteRole: {},
        MediaTypeList: [],
        isDeleteable: true,
        deleteDialogTitle: '',
        deleteDialogText: '',
        cannotDeleteDialogTitle: '',
        cannotDeleteDialogText: '',

        nameRules: [
          (v) => !!v || 'Media Type name is required',
        ],

        editNameRules: [
          (v) => !!v || 'Media Type name is required',
        ],

        tenantRules: [
          (v) => !!v || 'Tenant is required',
        ],
      }
    },
    async mounted() {
      if (!this.roleList || !this.roleList.length) {
        await this.getRoleList()
      }
      await this.getMediaTypeList()
    },
    computed: {
      ...stations.mapGetters(['iconList']),
      ...admin.mapGetters(['roleList', 'allMediaTypeList']),
      allRoleList() {
        return this.roleList.sort((a, b) => {
          return a.RoleName.toLocaleLowerCase().localeCompare(b.RoleName.toLocaleLowerCase())
        })
      },
    },
    watch: {
      allMediaTypeList() {
        if (this.roleList && this.roleList.length) {
          this.setMediaTypeList()
        }
      },
      roleList() {
        this.setMediaTypeList()
      },
    },
    methods: {
      ...admin.mapActions(['getRoleList', 'getMediaTypeList', 'postAddMediaType', 'putUpdateMediaType', 'deleteMediaType']),
      setMediaTypeList() {
        this.MediaTypeList = JSON.parse(JSON.stringify(this.allMediaTypeList))
        this.MediaTypeList.forEach(x => {
          x.TenantName = this.showTenantName(x.TenantId)
        })
      },
      showTenantName(id) {
        let target = this.roleList.find(x => (x.Id == id))
        return target.RoleName
      },
      addNewType() {
        if (this.$refs.form.validate()) {
          let name = this.newMediaType.name
          let tenant = this.newMediaType.TenantId.Id
          let targetTenant = []
          targetTenant = this.allMediaTypeList.filter(x => (x.TenantId == tenant && x.Name.toLowerCase() == name.toLowerCase()))
          if (targetTenant.length) {
            this.snackColor = 'info'
            this.snackMessage = `Media type ${name} in tenant ${this.newMediaType.TenantId.RoleName} already exists.`
            this.snackbar = true
          } else {
            let options = {
              data: {
                Id: null,
                Name: this.newMediaType.name,
                IconPath: this.newMediaType.IconPath ? this.newMediaType.IconPath : 'help',
                TenantId: this.newMediaType.TenantId.Id,
              },
              successCb: () => {
                this.snackMessage = this.$t('AdministrationMediaTypeManage.addNewTypeSuccessMsg', { msg: this.newMediaType.name })
                this.snackColor = 'success'
                this.snackbar = true
                this.clearForm()
              },
              failedCb: (err) => {
                this.snackColor = 'error'
                this.snackMessage = err
                this.snackbar = true
              },
            }
            this.postAddMediaType(options)
          }
        }
      },
      updateMediaType() {
        if (this.$refs.form2.validate()) {
          let name = this.editMediaType.Name
          let tenant = this.editMediaType.Tenant.Id
          let targetTenant = []
          targetTenant = this.typeListWithoutEdit.filter(x => (x.TenantId == tenant && x.Name.toLowerCase() == name.toLowerCase()))
          if (targetTenant.length) {
            this.snackColor = 'info'
            this.snackMessage = `Media type ${name} in tenant ${this.editMediaType.Tenant.RoleName} already exists.`
            this.snackbar = true
          } else {
            let options = {
              data: {
                Id: this.editMediaType.Id,
                Name: this.editMediaType.Name,
                IconPath: this.editMediaType.IconPath ? this.editMediaType.IconPath : 'help',
                TenantId: this.editMediaType.Tenant.Id,
              },
              successCb: () => {
                this.snackMessage = this.$t('AdministrationMediaTypeManage.updateMediaTypeSuccessMsg', { msg: this.editMediaType.Name })
                this.snackColor = 'success'
                this.snackbar = true
                this.clearForm()
              },
              failedCb: (err) => {
                this.snackColor = 'error'
                this.snackMessage = err
                this.snackbar = true
              },
            }
            this.putUpdateMediaType(options)
          }
        }
      },
      clickRemove(data) {
        axios.get('api/MediaTypes/DeleteChecking/' + data.Id)
          .then(res => {
            this.isDeleteable = res.data
            this.deleteRole = data
            if (res.data) {
              this.deleteDialogTitle = `Are you sure to remove Media Type: ${this.deleteRole.Name} of ${this.deleteRole.TenantName}?`
              this.deleteDialogText = 'If removed, data will not be recoverable.'
              this.dialog = true
            } else {
              this.cannotDeleteDialogTitle = 'Cannot remove media type with existing data'
              this.cannotDeleteDialogText = `Media type ${this.deleteRole.Name} for tenant ${this.deleteRole.TenantName} cannot be removed 
            because data exists for this media type.`
              this.cannotRemove = true
            }
          })
      },
      agreeDelete() {
        let options = {
          data: this.deleteRole.Id,
          successCb: () => {
            this.snackMessage = `Media type: ${this.deleteRole.Name} of ${this.deleteRole.TenantName} removed successfully.`
            this.snackColor = 'success'
            this.snackbar = true
            this.dialog = false
          },
          failedCb: (error) => {
            this.snackMessage = error.response.data.ExceptionMessage
            this.snackColor = 'error'
            this.snackbar = true
            this.dialog = false
          },
        }
        this.deleteMediaType(options)
      },
      clearForm() {
        this.$refs.form.reset()
        this.showAddType = false
        this.showUpdateType = false
      },
      clickEditType(data) {
        this.showUpdateType = true
        this.editMediaType = JSON.parse(JSON.stringify(data))
        this.editMediaType.Tenant = {
          Id: this.editMediaType.TenantId,
          RoleName: this.showTenantName(this.editMediaType.TenantId),
        }
        this.typeListWithoutEdit = this.removeEditType(data)
      },
      removeEditType(data) {
        let typeList = JSON.parse(JSON.stringify(this.allMediaTypeList))
        let index = typeList.findIndex(x => x.Id == data.Id)
        typeList.splice(index, 1)
        return typeList
      },
    },
  }
</script>

<style scoped>
  .toolbar__title.display-2 {
    padding-bottom: 10px;
    margin-left: 10px !important;
  }

  .fullScreenWidth {
    width: 100%;
  }

  .fontSize {
    font-size: 14px !important;
  }

  .titleAdjust {
    padding-top: 50px;
  }

  .iconAdjust {
    padding-bottom: 10px;
  }

  .snackbarAdjust {
    text-align: center;
  }
</style>